import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import WatchDemoModal, { DEMO_TOURS } from "@/components/landing/WatchDemoModal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Activity,
  Users,
  Calendar,
  Shield,
  FileText,
  CheckCircle,
  ArrowRight,
  Heart,
  Clock,
  BarChart3,
  Play,
  ChevronDown,
  MessageCircle,
  Send,
  X,
  Mail,
} from "lucide-react";
import axios from "axios";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// CarePilot contact info
const CAREPILOT_CONTACT = {
  email: "info@carepilotusa.com",
  demoCalendarLink: "https://cal.com/team/carepilot-usa/",
};

const features = [
  {
    icon: Users,
    title: "Client Management",
    description:
      "Track clients from referral to discharge with comprehensive profiles, status tracking, and medical history.",
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description:
      "Intuitive calendar-based scheduling with automated reminders and conflict detection.",
  },
  {
    icon: Shield,
    title: "HIPAA Compliant",
    description:
      "Enterprise-grade security with encrypted data storage and role-based access controls.",
  },
  {
    icon: FileText,
    title: "Referral Workflow",
    description:
      "Streamlined referral process with MD order tracking, fax integration, and assessment scheduling.",
  },
  {
    icon: Heart,
    title: "Clinical Alerts",
    description:
      "Automated alerts for diabetic monitoring, seizure protocols, and critical care interventions.",
  },
  {
    icon: BarChart3,
    title: "Analytics & Reports",
    description:
      "Real-time dashboards and customizable reports for agency performance insights.",
  },
];

const benefits = [
  "Multi-tenant architecture for agency isolation",
  "Role-based access (Admin, Case Manager, RN, Intake)",
  "Electronic Visit Verification (EVV) compliant",
  "CMS 485 form integration",
  "Billing and payroll management",
  "Document management system",
];



export default function LandingPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoTour, setDemoTour] = useState("full");
  const openDemo = (tour = "full") => { setDemoTour(tour); setDemoOpen(true); };
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", content: "Hi! I'm CarePilot's sales assistant. How can I help you learn about our home care management platform today?" }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/api/sales-chat`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at sales@carepilot.com" 
      }]);
    } finally {
      setChatLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-44 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/carepilot-logo.png" 
              alt="CarePilot" 
              className="h-40 w-auto"
            />
          </Link>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="hidden md:flex gap-2"
                  data-testid="nav-watch-demo-btn"
                >
                  <Play className="w-4 h-4" />
                  Watch Demo
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="nav-demo-menu">
                {DEMO_TOURS.map((t) => (
                  <DropdownMenuItem key={t.id} onClick={() => openDemo(t.id)} data-testid={`nav-demo-${t.id}`} className="flex flex-col items-start gap-0.5">
                    <span className="font-medium">{t.id === "full" ? "Full tour" : t.id === "fax" ? "Just the fax suite" : "Just clinical"}</span>
                    <span className="text-xs text-slate-500">{t.id === "full" ? "Every feature, ~5 min" : t.id === "fax" ? "Outbox, inbox, email-to-fax, orders" : "Assessments, care plans, CMS-485, notes"}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              variant="ghost" 
              data-testid="nav-login-btn"
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
            <Button 
              data-testid="nav-register-btn"
              onClick={() => navigate("/register")}
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="hero-gradient absolute inset-0 opacity-5 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Modern Home Care
                <span className="text-primary block">Management Platform</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
                Streamline your home care agency operations with our comprehensive
                platform. From referrals to billing, manage everything in one
                place.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="gap-2" 
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate("/register")}
                >
                  Start 7-Day Free Trial
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="gap-2"
                  onClick={() => openDemo("full")}
                  data-testid="hero-demo-btn"
                >
                  <Play className="w-4 h-4" />
                  Watch Demo
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>HIPAA Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>EVV Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>24/7 Support</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500" data-testid="hero-trial-note">7-day free trial · payment method required at signup · you won't be charged until the trial ends · cancel anytime</p>
            </div>

            {/* Hero Image/Illustration */}
            <div className="relative animate-slide-up">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&auto=format&fit=crop&q=80"
                  alt="Healthcare professional with patient"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
              </div>
              
              {/* Floating Clock Card */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100 animate-scale-in">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">40%</p>
                    <p className="text-xs text-slate-500">Time Saved</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              Everything You Need to Run Your Agency
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive tools designed specifically for home care agencies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-6 rounded-xl border border-slate-200 bg-white hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Built for Home Care Agencies
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Our platform is designed from the ground up to handle the unique
                challenges of home care operations.
              </p>

              <ul className="mt-8 space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="gap-2" 
                  data-testid="benefits-cta-btn"
                  onClick={() => navigate("/register")}
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="gap-2"
                  onClick={() => openDemo("full")}
                  data-testid="benefits-watch-demo-btn"
                >
                  <Play className="w-4 h-4" />
                  Watch Demo
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = `mailto:${CAREPILOT_CONTACT.email}?subject=CarePilot Inquiry`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Sales
                </Button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/5452228/pexels-photo-5452228.jpeg?w=800&auto=compress"
                alt="Healthcare team"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Ready to Transform Your Agency?
          </h2>
          <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">
            Join hundreds of home care agencies already using CarePilot to
            streamline their operations.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              variant="secondary"
              className="gap-2"
              data-testid="cta-get-started-btn"
              onClick={() => navigate("/register")}
            >
              Start 7-Day Free Trial
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10 gap-2"
              onClick={() => openDemo("full")}
              data-testid="cta-demo-btn"
            >
              <Play className="w-4 h-4" />
              Watch Demo
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10 gap-2"
              onClick={() => window.location.href = `mailto:${CAREPILOT_CONTACT.email}?subject=CarePilot Inquiry`}
              data-testid="cta-contact-btn"
            >
              <Mail className="w-4 h-4" />
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/carepilot-logo.png" 
                alt="CarePilot" 
                className="h-16 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} CarePilot. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <WatchDemoModal open={demoOpen} onOpenChange={setDemoOpen} tour={demoTour} onTourChange={setDemoTour} />

      {/* AI Sales Chat Widget */}
      {chatOpen && (
        <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50 animate-scale-in">
          {/* Chat Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-primary text-white rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Sales Assistant</h3>
                <p className="text-xs text-white/80">Powered by AI</p>
              </div>
            </div>
            <button 
              onClick={() => setChatOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === "user"
                      ? "bg-primary text-white rounded-br-md"
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat Input */}
          <div className="p-4 border-t border-slate-200">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex gap-2"
            >
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask about CarePilot..."
                className="flex-1"
                disabled={chatLoading}
              />
              <Button type="submit" size="icon" disabled={chatLoading || !chatInput.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Chat Toggle Button (when closed) */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-4 right-4 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors z-50 hover:scale-110"
          data-testid="chat-toggle-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
